<template>
  <card-wrapper class="drawer-wrapper card overflow-hidden"
                :class="[
                  fullscreenOverlay ? 'absolute' : 'relative',
                  { 'bg-transparent': fullscreenOverlay }
                ]"
  >
    <div v-if="showDrawer && fullscreenOverlay"
         class="w-screen h-screen absolute bg-transparent top-0 left-0 z-20"
         @click="closeDrawer"
    />

    <div v-if="!fullscreenOverlay" @click="closeDrawer">
      <slot name="card-content"/>
    </div>

    <div v-if="showDrawer"
         class="absolute drawer h-full shadow-l-xl bg-white z-30"
         :class="[
           `${drawerAnchor}-0 ${animationClass}`,
           { 'min-w-136': fullscreenOverlay }
         ]"
         :style="`width: ${drawerWidth};`"

    >
      <close-icon class="text-gray-dark absolute close-icon cursor-pointer z-20" @click.native="closeDrawer"/>
      <slot name="drawer-content"/>
    </div>
  </card-wrapper>
</template>

<script>
import CardWrapper from '@/components/base-components/CardWrapper'
import CloseIcon from '@/assets/images/icons/CloseIcon'
import domUtils from '@/utils/dom.utils'

export default {
  name: 'DrawerCard',

  components: { CardWrapper, CloseIcon },

  props: {
    value: {
      type: Boolean,
      required: true
    },
    drawerWidth: {
      type: String, // XXpx, XXrem, XX% etc.
      default: '80%'
    },
    drawerAnchor: {
      type: String,
      default: 'right' // or left
    },
    fullscreenOverlay : {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      animationClass: this.getAnimationClass('in')
    }
  },

  computed: {
    showDrawer: {
      get() {
        return this.value
      },
      set(value) {
        // use v-model in parent
        this.$emit('input', value)
      }
    }
  },

  methods: {
    async closeDrawer(e) {
      // to prevent drawer closing after click on open drawer button
      if (e.target.id.includes('show-drawer')) return

      this.animationClass = this.getAnimationClass('out')
      // wait till slide out animation finish
      await new Promise(resolve => setTimeout(resolve, 500))
      this.showDrawer = false
      this.animationClass = this.getAnimationClass('in')
    },

    getAnimationClass(direction) {
      return `slide-${direction}-${this.drawerAnchor}`
    }
  },

  mounted() {
    if (this.fullscreenOverlay) domUtils.moveToFullscreenOverlay(this.$el)
  },

  destroyed() {
    if (this.fullscreenOverlay) domUtils.clearFullscreenOverlay(this.$el)
  }
}
</script>

<style lang="scss">
.card {
  min-height: min-content;

  .vs-card--content {
    margin: 0 !important;
  }

  .drawer {
    top: 0;
    border-radius: 8px;
  }

  .close-icon {
    top: 20px;
    left: 15px;
  }

  .slide-in-right {
    animation: slide-in-right 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
  }

  @keyframes slide-in-right {
    0% {
      transform: translateX(1000px);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .slide-out-right {
    animation: slide-out-right 0.5s cubic-bezier(0.550, 0.085, 0.680, 0.530) both;
  }

  @keyframes slide-out-right {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform: translateX(1000px);
      opacity: 0;
    }
  }

  .slide-in-left {
    animation: slide-in-left 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
  }

  @keyframes slide-in-left {
    0% {
      transform: translateX(-1000px);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .slide-out-left {
    animation: slide-out-left 0.5s cubic-bezier(0.550, 0.085, 0.680, 0.530) both;
  }

  @keyframes slide-out-left {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform: translateX(-1000px);
      opacity: 0;
    }
  }
}
</style>
